import React, { useCallback, useMemo, useEffect, type PropsWithChildren } from 'react';
import {
	graphql,
	useFragment,
	EntryPointContainer,
	type EntryPoint,
	type EntryPointProps,
	type PreloadedEntryPoint,
	type PreloadedQuery,
} from 'react-relay';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { ContextualAnalyticsData } from '@jira/navigation-apps__sidebar-common/src/ui/contextual-analytics-data/index.tsx';
import { useShowRoadmapsUpsell as useShowRoadmapsUpsellNew } from '@jira/growth__jira-roadmap-in-nav/src/useShowRoadmapsUpsell.tsx';
import type { useShowRoadmapsUpsell$key } from '@jira/growth__jira-roadmap-in-nav/src/__generated__/useShowRoadmapsUpsell.graphql.ts';
import { GlobalSideNavigation } from '@atlassian/global-side-navigation/GlobalSideNavigation';
import type { ForYouConfig } from '@atlassian/global-side-navigation/for-you-types';
import type {
	GlobalSideNavigationProps,
	ShortcutsConfig,
} from '@atlassian/global-side-navigation/common/types';
import type { ObjectListFlyoutItem } from '@atlassian/navigation-common/types';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { useFilterL1MenuItemsByEditionAndLicense } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/controllers/use-filter-l1-menu-items-by-edition-and-license/index.tsx';
import {
	USER_NAVIGATION_CONFIGURATION_DEFAULT,
	USER_NAVIGATION_CONFIGURATION_DEFAULT_GOALS_TEAMS_SWITCHED,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/common/constants/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { expVal } from '@atlassian/jira-feature-experiments';
import { RECENT_QUERY_LIMIT } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-recent/src/common/constants.tsx';
import {
	FAVOURITE_QUERY_LIMIT,
	VALID_FAVORITE_TYPES,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-content-starred/src/common/constants.tsx';
import { useFilterMenuItems } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-starred/src/common/controllers/use-filter-menu-items/index.tsx';
import type { UseInvitedAppShortcutsResult } from '@atlassian/cross-join-nudges/services/invited-app-shortcuts/invited-app-shortcuts';
import { fg } from '@atlassian/jira-feature-gating';
import { useResolveInvitedAppShortcuts } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/ui/configurable-menu-items/global-shortcuts/useResolveInvitedAppShortcuts.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import type { InvitedAppShortcutsRecommendationsComponent } from './InvitedAppShortcutsRecommendations.tsx';
import { Nav4JiraSideNavRecommendation } from '@jira/growth__side-nav-recommendation/src/index.tsx';
import { MENU_ID_FOR_YOU } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { getForYouConfig } from './getForYouConfig.tsx';
import {
	InvitedAppShortcutsSlot,
	InvitedAppShortcutsSlotContext,
	type InvitedAppShortcutsSlotConfig,
} from './InvitedAppShortcutsSlot.tsx';
import { useShortcuts } from './useShortcuts.tsx';
import { useItemsConfig } from './useItemsConfig.tsx';
import { GSNQueryPreloader } from './GSNQueryPreloader.tsx';
import type {
	GSNQueryPreloaderQuery,
	GSNQueryPreloaderQuery$data,
} from './__generated__/GSNQueryPreloaderQuery.graphql.ts';
import { useNetworkStorageParams } from './useNetworkStorageParams.tsx';
import { useSaveCurrentContentSidebarRoute } from './useSaveCurrentContentSidebarRoute.tsx';
import { SIDEBAR_TEST_ID } from './sidebarTestId.constants.tsx';
import { usePeekMenuId } from './usePeekMenuId.tsx';
import type { GSNForContentSidebarEntryPoint$key } from './__generated__/GSNForContentSidebarEntryPoint.graphql.ts';

type EntryPointParams = EntryPointProps<
	// gsnSidebarContent is optional in the entrypoint type because anonymous users receive an empty
	// queries object. However, NavPlatformSidebar (the parent) ensures this component is never
	// rendered for anonymous users, so gsnSidebarContent is always defined here at runtime.
	{ gsnSidebarContent?: GSNQueryPreloaderQuery },
	// Child entry points preloaded by the parent. Optional: present only when
	// the productionisation cohort gates are on for an authenticated user.
	// Structural `EntryPoint<Component, RuntimeProps>` form (not `typeof <const>`)
	// to comply with `jira/thunderstone/no-types-from-typeof`.
	{
		invitedAppShortcutsRecommendations?: EntryPoint<
			InvitedAppShortcutsRecommendationsComponent,
			{ cloudId: string }
		>;
	},
	{},
	{ isShortcutsStatic?: boolean } | null
>;

export function GSNForContentSidebarEntryPoint(props: EntryPointParams): React.JSX.Element {
	// This EP has the preloaded query-ref, to pass to the component that will usePreloadedQuery().
	// NavPlatformSidebar guarantees this component is only rendered for authenticated users,
	// so gsnSidebarContent is always defined (non-null assertion is safe here).
	return (
		<GSNForContentSidebar
			// NavPlatformSidebar guarantees this component is only rendered for authenticated users,
			// so gsnSidebarContent is always defined at runtime. Cast is safe.
			// oxlint-disable-next-line typescript/consistent-type-assertions
			queryReference={props.queries.gsnSidebarContent as PreloadedQuery<GSNQueryPreloaderQuery>}
			invitedAppShortcutsRecommendationsEntryPoint={
				props.entryPoints?.invitedAppShortcutsRecommendations
			}
			isShortcutsStatic={props.extraProps?.isShortcutsStatic}
		/>
	);
}
export default GSNForContentSidebarEntryPoint;

interface Props {
	queryReference: PreloadedQuery<GSNQueryPreloaderQuery>;
	/**
	 * Optional preloaded entry point reference for the AGG recommendations
	 * query. Only defined when the productionisation cohort gates fire in the
	 * parent `gsnSidebarContentEntryPoint.entrypoint.tsx`. When `undefined`,
	 * the inner dispatch falls through to the experiment / control branch.
	 */
	invitedAppShortcutsRecommendationsEntryPoint?: PreloadedEntryPoint<InvitedAppShortcutsRecommendationsComponent>;
	isShortcutsStatic?: boolean;
	/**
	 * Allow this provider to be mocked (Storybook has its own)
	 * @private
	 * @deprecated - The provider now appears higher in the component tree (around the NavPlatformSidebar),
	 *               so there is no need to mock it here.
	 */
	MaybeSidebarNav4ContextProvider?: React.FC<PropsWithChildren>;
}

export function GSNForContentSidebar(props: Props): React.JSX.Element {
	// Add support for the Back button inside non-content sidebars, by storing the previous URL when the content sidebar is showing.
	// To test this component, make sure that it is wrapped in NavigateBackProvider
	useSaveCurrentContentSidebarRoute();

	const renderFn = useCallback(
		({ queryReference }: { queryReference: GSNQueryPreloaderQuery$data }) => (
			<GSNForContentSidebarInner
				queryReference={queryReference}
				invitedAppShortcutsRecommendationsEntryPoint={
					props.invitedAppShortcutsRecommendationsEntryPoint
				}
				isShortcutsStatic={props.isShortcutsStatic}
			/>
		),
		[props.invitedAppShortcutsRecommendationsEntryPoint, props.isShortcutsStatic],
	);

	return <GSNQueryPreloader queryReference={props.queryReference} render={renderFn} />;
}

interface InnerProps {
	invitedAppShortcutsRecommendationsEntryPoint?: PreloadedEntryPoint<InvitedAppShortcutsRecommendationsComponent>;
	isShortcutsStatic?: boolean;
	queryReference: GSNForContentSidebarEntryPoint$key;
}

const useShowRoadmapsUpsell = functionWithCondition(
	() => fg('jpd-roadmaps-in-jira'),
	(data: useShowRoadmapsUpsell$key) => useShowRoadmapsUpsellNew(data),
	() => false,
);

// This is the component that should be referenced in Storybook.
// Each sidebar can have its own overrides (e.g. isShortcutsStatic)
function GSNForContentSidebarInner(props: InnerProps) {
	const {
		isShortcutsStatic = false,
		queryReference,
		invitedAppShortcutsRecommendationsEntryPoint,
	} = props;

	// eslint-disable-next-line @atlassian/relay/use-fragment-naming, @atlassian/relay/use-fragment-naming
	const data = useFragment(
		// Need to disable this rule for fragments that are used by child components
		/* eslint-disable @atlassian/relay/must-colocate-fragment-spreads */
		/* eslint-disable @atlassian/relay/unused-fields */
		graphql`
			fragment GSNForContentSidebarEntryPoint on Query {
				...useNetworkSideNavUserData
				...useShowRoadmapsUpsell @dangerously_unaliased_fixme @include(if: $isRoadmapUpsellEnabled)
				...useGlobalShortcutsDataRelay
					@dangerously_unaliased_fixme
					@include(if: $fetchGlobalShortcutsAvailableProducts)
				navPlatL1MenuData: settings_navigationCustomisation(
					entityAri: $navPlatMenuDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				navPlatSidebarData: settings_navigationCustomisation(
					entityAri: $navPlatSidebarDataEntityAri
					ownerAri: $ownerAri
				) {
					...useNavSettingsService
				}
				...Nav4Apps
				...Nav4Filters
				...Nav4Operations
				...Nav4Plans
				...Nav4Projects
				...Nav4Roadmaps_query
			}
		`,
		// eslint-disable-next-line @atlassian/relay/use-fragment-naming
		queryReference,
	);

	const networkStorageParamsPartial = useNetworkStorageParams();
	const networkStorageParams = useMemo(() => {
		return { ...networkStorageParamsPartial, queryRef: data };
	}, [data, networkStorageParamsPartial]);
	const { siteId: cloudId } = networkStorageParams;
	const { isSelectedPath } = useSidebarNav4(); // This is needed to support peeking / peekMenuId

	// Items ------
	const shouldShowRoadmapsUpsell = useShowRoadmapsUpsell(data);

	const navItems = useFilterL1MenuItemsByEditionAndLicense(
		expVal('navx-1982-change-order-of-teams-and-goals', 'isEnabled', false)
			? USER_NAVIGATION_CONFIGURATION_DEFAULT_GOALS_TEAMS_SWITCHED.navItems
			: USER_NAVIGATION_CONFIGURATION_DEFAULT.navItems,
		{ shouldShowRoadmapsUpsell },
	);

	const items = useItemsConfig(navItems, data);

	// Shortcuts
	const shortcuts = useShortcuts();

	// ForYou config --------
	const recentEntryPointParams = useMemo(() => ({ cloudId, count: RECENT_QUERY_LIMIT }), [cloudId]);
	const starredEntryPointParams = useMemo(
		() => ({
			cloudId,
			filter: { types: VALID_FAVORITE_TYPES, sort: { order: 'DESC' as const } },
			count: FAVOURITE_QUERY_LIMIT,
		}),
		[cloudId],
	);
	const { formatMessage } = useIntl();
	const starredFilterMenuItemDataRaw = useFilterMenuItems();
	const starredFilterConfig = useMemo(
		() =>
			starredFilterMenuItemDataRaw.map((item) => ({
				key: item.filterType,
				label: formatMessage(item.message),
				elemBefore: <item.Icon />,
			})),
		[formatMessage, starredFilterMenuItemDataRaw],
	);

	const isForYouSelected = isSelectedPath(MENU_ID_FOR_YOU);
	const forYouConfig = useMemo(
		() =>
			// oxlint-disable-next-line typescript/consistent-type-assertions
			getForYouConfig({
				isForYouSelected,
				recentEntryPointParams,
				starredEntryPointParams,
				starredFilterConfig,
			}) as ForYouConfig<ObjectListFlyoutItem>,
		[isForYouSelected, recentEntryPointParams, starredEntryPointParams, starredFilterConfig],
	);

	// Peek Menu ------
	// Determine peekMenuId from selectedPath
	const peekMenuIdWithRouteFilter = usePeekMenuId(navItems);
	const peekMenuId = expVal('uxissue-1664-autoexpand-spaces', 'isEnabled', false)
		? peekMenuIdWithRouteFilter
		: navItems.find((item) => isSelectedPath(item.menuId))?.menuId;

	// Note: When Jira GSN adoption rolls out, it should rollout with "network-migration" for 3 months
	//  to give time for all old user settings to be stored in the new records.
	//
	//    - customizationStore="network-migration"
	//
	//  After 3 months of being rolled-out, create a new FG to:
	//   - Change customizationStore to "network"
	//   - @include(if: $fgVariable) for useNetworkSideNavUserData.
	//   - After that has rolled out, cleanup
	// `as const` so it matches the literal-union type
	// (`'network-migration' | 'local' | 'network'`) expected downstream.
	const customizationStore = 'network-migration' as const;

	const bottomElem = useMemo(
		() => (fg('gsn-jira-side-nav-in-footer') ? <Nav4JiraSideNavRecommendation /> : undefined),
		[],
	);

	// `as const` so it matches the `'inherit'` literal type expected downstream.
	const intlMode = 'inherit' as const;

	// `useAccountId` at the top of the hook order (Rules of Hooks).
	const userId = useAccountId();

	// TWCG-460 productionisation dispatch. Not gated on any experiment: the
	// preloaded entrypoint is only present when the kill-switch gate is on
	// (checked in the parent entrypoint), and the preload experiment exposure is
	// fired in `GSNForContentSidebarWithInvitedAppRecommendations.tsx` once recs
	// render — reading it here would expose before the treatment shows.
	const willDispatchProductionisation = Boolean(
		cloudId && userId && invitedAppShortcutsRecommendationsEntryPoint,
	);

	// Shortcuts Experiment -----
	const [isInvitedAppShortcutsEnabled, invitedAppShortcutsExperiment, onShortcutsMenuSettled] =
		useIsInvitedAppshortcutsEnabled({ willDispatchProductionisation });

	// Config for the `ShortcutsSlot` (experiment/legacy branch). Passed to the
	// module-level `InvitedAppShortcutsSlot` via context so the slot keeps a
	// stable identity and GSN never remounts the shortcuts subtree. Memoised to
	// keep the context value referentially stable across renders.
	const experimentSlotConfig = useMemo<InvitedAppShortcutsSlotConfig>(
		() => ({ mode: 'experiment', resolverResult: invitedAppShortcutsExperiment }),
		[invitedAppShortcutsExperiment],
	);

	// Stable forwarded prop bag for the productionisation `EntryPointContainer`.
	// Memoised to satisfy `@atlassian/perf-linting/no-unstable-inline-props`
	// and keep downstream `React.memo` identity stable. Runs unconditionally
	// to keep hook order stable.
	// `userId ?? ''` is type-narrowing only: the consuming JSX is gated by
	// `willDispatchProductionisation` which already requires `userId` to be truthy.
	const invitedAppRecommendationsContainerProps = useMemo(
		() => ({
			bottomElem,
			cloudId,
			customizationStore,
			experimental_onShortcutsMenuSettled: onShortcutsMenuSettled,
			forYouConfig,
			intlMode,
			isShortcutsStatic,
			items,
			networkStorageParams,
			peekMenuId,
			shortcuts,
			testId: SIDEBAR_TEST_ID,
			userId: userId ?? '',
		}),
		[
			bottomElem,
			cloudId,
			customizationStore,
			onShortcutsMenuSettled,
			forYouConfig,
			intlMode,
			isShortcutsStatic,
			items,
			networkStorageParams,
			peekMenuId,
			shortcuts,
			userId,
		],
	);

	// Render the legacy experiment branch or the plain control. Used both as
	// the regular fallthrough and as the productionisation `JSErrorBoundary`
	// fallback (so a transport error on the recommendations query degrades to
	// the same sidebar the gate-off cohort would see).
	const renderLegacyOrControlSidebar = useCallback((): React.JSX.Element => {
		// GSN always receives the base `shortcuts` config. When the invited-app
		// shortcuts experiment is enabled, the decoration happens inside GSN's
		// `ShortcutsSlot` (`InvitedAppShortcutsSlot`) rather than by swapping the
		// whole `shortcuts` prop, so SSR<->CSR flips of the experiment no longer
		// restructure the nav tree.
		const sharedProps: GlobalSideNavigationProps = {
			app: 'jira' as const,
			appLabel: 'Jira',
			bottomElem,
			cloudId,
			customizationStore,
			forYou: forYouConfig,
			intlMode,
			isShortcutsStatic, // Needed for VR tests as the URL can't be mocked
			items,
			networkStorageParams,
			peekMenuId,
			shortcuts,
			testId: SIDEBAR_TEST_ID,
			...(fg('twcg-460-personalised-invite-notifications-m2-gate') && {
				experimental_onShortcutsMenuSettled: onShortcutsMenuSettled,
			}),
		};

		if (isInvitedAppShortcutsEnabled) {
			return (
				<InvitedAppShortcutsSlotContext.Provider value={experimentSlotConfig}>
					<GlobalSideNavigation {...sharedProps} ShortcutsSlot={InvitedAppShortcutsSlot} />
				</InvitedAppShortcutsSlotContext.Provider>
			);
		}

		return <GlobalSideNavigation {...sharedProps} />;
	}, [
		bottomElem,
		cloudId,
		customizationStore,
		experimentSlotConfig,
		forYouConfig,
		intlMode,
		isInvitedAppShortcutsEnabled,
		isShortcutsStatic,
		items,
		networkStorageParams,
		onShortcutsMenuSettled,
		peekMenuId,
		shortcuts,
	]);

	// The second clause is type-narrowing only (`willDispatchProductionisation`
	// already requires the entry point to be defined). The `JSErrorBoundary`
	// fails open to the legacy / control branch so transport errors on the
	// recommendations query don't take down the sidebar.
	if (willDispatchProductionisation && invitedAppShortcutsRecommendationsEntryPoint) {
		// `ContextualAnalyticsData` is required so the `JSErrorBoundary` can
		// emit analytics events when it catches (per the AFM `handling-errors`
		// skill — without it the boundary silently swallows the error).
		return (
			<ContextualAnalyticsData menuId="gsn-invited-app-shortcuts-productionisation">
				<JSErrorBoundary
					id="gsn-invited-app-shortcuts-productionisation"
					packageName="jiraNavPlatformIntegrationSidebar"
					teamName="growth-puma"
					fallback={renderLegacyOrControlSidebar}
				>
					<EntryPointContainer
						entryPointReference={invitedAppShortcutsRecommendationsEntryPoint}
						props={invitedAppRecommendationsContainerProps}
					/>
				</JSErrorBoundary>
			</ContextualAnalyticsData>
		);
	}

	return renderLegacyOrControlSidebar();
}

function useIsInvitedAppshortcutsEnabled({
	willDispatchProductionisation,
}: {
	willDispatchProductionisation: boolean;
}): [
	boolean,
	UseInvitedAppShortcutsResult,
	(input: { shortcuts: ShortcutsConfig; visibleMenuIds: string[] }) => void,
] {
	const invitedAppShortcutsExperiment = useResolveInvitedAppShortcuts();

	const isEligibleToSeeInvitedAppShortcuts =
		// we split the kill-switch gate and targeting gate to avoid the overrides precedence issue
		// eslint-disable-next-line jira/ff/no-preconditioning
		fg('twcg-460-personalised-invite-notifications-m2-gate') &&
		invitedAppShortcutsExperiment.ineligibilityReason === null &&
		invitedAppShortcutsExperiment.invitedApps?.confluence?.eligible === true;

	const isInvitedAppShortcutsEnabled =
		isEligibleToSeeInvitedAppShortcuts &&
		expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false);

	useEffect(() => {
		if (isEligibleToSeeInvitedAppShortcuts) {
			if (willDispatchProductionisation) {
				invitedAppShortcutsExperiment.fireFeatureExposed({
					// technically, we will only fire the exposures for twcg-460-preload-grs-invited-app-shortcuts's control cohort here
					isEnabled: expVal('twcg-460-preload-grs-invited-app-shortcuts', 'isEnabled', false),
					overrideMode: 'productionisation',
				});
			} else {
				invitedAppShortcutsExperiment.fireFeatureExposed({
					isEnabled: expVal('twcg-460-personalised-invite-notifications-m2', 'isEnabled', false),
				});
			}
		}
	}, [
		isEligibleToSeeInvitedAppShortcuts,
		invitedAppShortcutsExperiment,
		willDispatchProductionisation,
	]);

	const onShortcutsMenuSettled = useCallback(
		({
			shortcuts: hydratedShortcuts,
			visibleMenuIds,
		}: {
			shortcuts: ShortcutsConfig;
			visibleMenuIds: string[];
		}) => {
			invitedAppShortcutsExperiment.updateFromHydratedShortcuts({
				shortcuts: hydratedShortcuts,
				limitToOnlyApsKeys: ['CONFLUENCE'],
				visibleMenuIds,
			});
		},
		[invitedAppShortcutsExperiment],
	);

	return [isInvitedAppShortcutsEnabled, invitedAppShortcutsExperiment, onShortcutsMenuSettled];
}
